import React from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
// import axiosInstance from "../services/axiosInstance";

const getQuestionKey = (question) => question?._id || question?.id || question?.questionId;

const normalizeDifficulty = (difficulty) => {
  if (!difficulty) return "";
  const normalized = String(difficulty).trim().toUpperCase();
  if (normalized === "EASY") return "Easy";
  if (normalized === "MEDIUM") return "Medium";
  if (normalized === "HARD") return "Hard";
  return difficulty;
};

const buildDefaultMarks = (question) => ({
  part1MaxMarks: Number(question?.parts?.part1?.maxMarks ?? 0),
  part2MaxMarks: Number(question?.parts?.part2?.maxMarks ?? 0)
});

const AllowedQuestionsSection = ({
  value,
  onChange,
  disabled = false,
  showHeader = true,
  questionsProp,
  /** @type {string[]} */
  evaluationSetKeys = []
}) => {
  const [questions, setQuestions] = React.useState(questionsProp);
  const [error, setError] = React.useState("");
  const [internalValue, setInternalValue] = React.useState(Array.isArray(value) ? value : []);
  // Sync internalValue with value prop (for edit mode default selection)
  React.useEffect(() => {
    if (Array.isArray(value)) {
      setInternalValue(value);
    }
  }, [value]);

  const allowedQuestions = Array.isArray(value) ? value : internalValue;

  const emitChange = (nextValue) => {
    if (onChange) {
      onChange(nextValue);
    } else {
      setInternalValue(nextValue);
    }
  };

  React.useEffect(() => {
    if (Array.isArray(questionsProp)) {
      setQuestions(questionsProp);
      setError("");
    }
  }, [questionsProp]);

  const selectedMap = React.useMemo(() => {
    const map = new Map();
    allowedQuestions.forEach((entry) => {
      map.set(String(entry.questionId), entry);
    });
    return map;
  }, [allowedQuestions]);

  const handleToggle = (question) => {
    const questionId = getQuestionKey(question);
    const next = [...allowedQuestions];

    if (selectedMap.has(String(questionId))) {
      emitChange(next.filter((item) => String(item.questionId) !== String(questionId)));
      return;
    }

    const defaults = buildDefaultMarks(question);
    const defaultSetKey = evaluationSetKeys.length > 0 ? evaluationSetKeys[0] : "";
    next.push({
      questionId,
      setKey: defaultSetKey,
      part1MaxMarks: defaults.part1MaxMarks,
      part2MaxMarks: defaults.part2MaxMarks
    });
    emitChange(next);
  };

  const handleMarksChange = (questionId, field, valueInput) => {
    const next = allowedQuestions.map((item) => {
      if (String(item.questionId) !== String(questionId)) return item;
      return {
        ...item,
        [field]: valueInput === '' ? '' : Number(valueInput)
      };
    });
    emitChange(next);
  };

  const handleSetKeyChange = (questionId, setKey) => {
    const next = allowedQuestions.map((item) => {
      if (String(item.questionId) !== String(questionId)) return item;
      return { ...item, setKey };
    });
    emitChange(next);
  };

  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      {showHeader && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Allowed Questions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Select questions, map each to an evaluation rubric set (SET_1, …), and set Part 1 / Part 2 max marks.
          </Typography>
        </Box>
      )}

      {/* No loading spinner needed since questions are always passed as a prop */}
      {error && (
        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <TableContainer sx={{ maxHeight: 250 }}>
        <Table size="small" aria-label="allowed questions table">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Question ID</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Difficulty</TableCell>
              <TableCell>Evaluation set</TableCell>
              <TableCell>Part 1 Max Marks</TableCell>
              <TableCell>Part 2 Max Marks</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {questions.map((question) => {
              const questionId = getQuestionKey(question);
              const selected = selectedMap.has(String(questionId));
              const selectedEntry = selectedMap.get(String(questionId));

              return (
                <TableRow key={questionId} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selected}
                      onChange={() => handleToggle(question)}
                      disabled={disabled}
                    />
                  </TableCell>
                  <TableCell>{question.questionId || question.id}</TableCell>
                  <TableCell>{question.title}</TableCell>
                  <TableCell>{normalizeDifficulty(question.difficulty)}</TableCell>
                  <TableCell sx={{ minWidth: 140 }}>
                    <FormControl size="small" fullWidth disabled={!selected || disabled}>
                      <InputLabel id={`setkey-label-${questionId}`}>Set</InputLabel>
                      <Select
                        labelId={`setkey-label-${questionId}`}
                        label="Set"
                        value={selectedEntry?.setKey ?? ""}
                        onChange={(e) => handleSetKeyChange(questionId, e.target.value)}
                      >
                        {evaluationSetKeys.map((key) => (
                          <MenuItem key={key} value={key}>
                            {key}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      inputProps={{ min: 1 }}
                      value={selectedEntry?.part1MaxMarks ?? ""}
                      onChange={(event) =>
                        handleMarksChange(questionId, "part1MaxMarks", event.target.value)
                      }
                      disabled={!selected || disabled}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      inputProps={{ min: 1 }}
                      value={selectedEntry?.part2MaxMarks ?? ""}
                      onChange={(event) =>
                        handleMarksChange(questionId, "part2MaxMarks", event.target.value)
                      }
                      disabled={!selected || disabled}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default AllowedQuestionsSection;
